import { LayoutUnit } from '../models/basic/layoutunit.model';
import { SideNav } from '../models/controls/sidenav.model';
import { GradientType } from '../models/enums/gradienttype.enum';
import { Overflow } from '../models/enums/overflow.enum';
import { UnitType } from '../models/enums/unittype.enum';
import { Layout } from '../models/layout.model';
import { LayoutBase } from '../models/layoutbase.model';
import { LayoutElement } from '../models/layoutelement.model';
import { Color } from '../models/style/color.model';
import { Font } from '../models/style/font.model';
import { Gradient } from '../models/style/gradient.model';
import { GradientStopColor } from '../models/style/gradientstopcolor.model';
import { TranslatedString } from '../models/translatedstring.model';
import { SideNavService } from '../services/sidenav.service';
import { PermissionHelper } from './permissions.helper';

export class NavigationHelper {

    static BuildStructure(naviNodes: any[]) {
        const root = new NavigationStructureItem(null);
        const otherNodes: NavigationStructureItem[] = [];
        const nodeMap = new Map<string, NavigationStructureItem>();
        naviNodes.forEach(nn => {
            const node = new NavigationStructureItem(nn);
            nodeMap.set(nn._Id, node);
            if (nn.ParentID) {
                const parent = nodeMap.get(nn.ParentID);
                if (parent) {
                    parent.AddChild(node);
                } else {
                    otherNodes.push(node);
                }
            } else {
                root.AddChild(node);
            }
        });
        otherNodes.forEach(node => {
            const parent = nodeMap.get(node.ParentID);
            if (parent) {
                parent.AddChild(node);
            }
        });
        return root.GetStructure().Children;
    }

    static BuildNavigation(naviNodes: any[]) {
        const structure = NavigationHelper.BuildStructure(naviNodes);
        const perm = PermissionHelper.ActiveNavigationPermissions.getValue();
        return {
            NavigationItems: NavigationHelper.GetNavigationItems(structure, perm)
        };
    }

    private static GetNavigationItems(structList: any[], perm: any) {
        const retVal = [];
        if (structList) {
            structList.forEach(x => {
                const node = {
                    Id: x.Data._Id,
                    Label: TranslatedString.GetTranslation(x.Data.Caption),
                    Url: null,
                    Icon: null,
                    children: null,
                    IsVisible: true,
                    InVisible: false
                };
                if (x.Data.Url) {
                    if (SideNavService.IsSystemURL(x.Data.Url)) {
                        node.Url = x.Data.Url;
                    } else {
                        node.Url = '/' + x.Data.StructureKey + x.Data.Url;
                    }
                }
                if (x.Data.Icon) {
                    node.Icon = x.Data.Icon;
                }
                if (perm) {
                    const actPerm = perm[node.Id];
                    if (typeof actPerm === 'boolean') {
                        node.InVisible = actPerm;
                    }
                }
                if (x.Data.Hidden) {
                    node.InVisible = true;
                }
                const children = NavigationHelper.GetNavigationItems(x.Children, perm);
                if (children.length > 0) {
                    node.children = children;
                }
                retVal.push(node);
            });
        }
        return retVal;
    }

    static GetDefaultBaseLayout(): Layout {
        const l = new Layout();

        l.Visible = true;
        l.Name = 'BaseLayout';
        l['ElementType'] = 'grid';
        l['SideNav'] = SideNav.GetDefault();

        const outerCol = new LayoutBase();
        outerCol.Size = new LayoutUnit();
        outerCol.Size.Type = UnitType.Rest;
        l['ColumnDefinitions'] = [outerCol];

        const outerFirstRow = new LayoutBase();
        outerFirstRow.Size = new LayoutUnit();
        outerFirstRow.Size.Type = UnitType.Pixel;
        outerFirstRow.Size.Value = 50;
        const outerSecondRow = new LayoutBase();
        outerSecondRow.Size = new LayoutUnit();
        outerSecondRow.Size.Type = UnitType.Rest;
        l['RowDefinitions'] = [outerFirstRow, outerSecondRow];

        const innerGrid = new LayoutElement();

        innerGrid.Visible = true;
        innerGrid.Name = 'Header';
        innerGrid.ElementType = 'grid';

        innerGrid.BackgroundColor = new Gradient();
        innerGrid.BackgroundColor.Type = GradientType.Solid;
        const color = new GradientStopColor();
        color.Color = Color.FromHex('#5E5E5E');
        innerGrid.BackgroundColor.Colors.push(color);

        innerGrid.Font = new Font();
        innerGrid.Font.FontColor = Color.FromHex('#F5F5F5');

        const firstCol = new LayoutBase();
        firstCol.Size = new LayoutUnit();
        firstCol.Size.Type = UnitType.Pixel;
        firstCol.Size.Value = 50;
        const secCol = new LayoutBase();
        secCol.Size = new LayoutUnit();
        secCol.Size.Type = UnitType.Rest;
        const thirdCol = new LayoutBase();
        thirdCol.Size = new LayoutUnit();
        thirdCol.Size.Type = UnitType.Pixel;
        thirdCol.Size.Value = 50;
        const fourthCol = new LayoutBase();
        fourthCol.Size = new LayoutUnit();
        fourthCol.Size.Type = UnitType.Pixel;
        fourthCol.Size.Value = 50;

        innerGrid['ColumnDefinitions'] = [firstCol, secCol, thirdCol, fourthCol];

        const innerRow = new LayoutBase();
        innerRow.Size = new LayoutUnit();
        innerRow.Size.Type = UnitType.Rest;

        innerGrid['RowDefinitions'] = [innerRow];

        const sideNavToggle = new LayoutElement();
        sideNavToggle.Name = 'sidenav-toggle_1';
        sideNavToggle.Visible = true;
        sideNavToggle.Editable = true;
        sideNavToggle.ElementType = 'sidenav-toggle';
        innerGrid.Elements.push(sideNavToggle);

        const messageSetting = new LayoutElement();
        messageSetting.Name = 'messagecenter_1';
        messageSetting.Visible = true;
        messageSetting.Editable = true;
        messageSetting.ElementType = 'messagecenter';
        messageSetting.Column = 3;
        innerGrid.Elements.push(messageSetting);

        const userMenu = new LayoutElement();
        userMenu.Name = 'usermenu_1';
        userMenu.Visible = true;
        userMenu.Editable = true;
        userMenu.ElementType = 'usermenu';
        userMenu.Column = 4;
        innerGrid.Elements.push(userMenu);

        l.Elements.push(innerGrid);

        const content = new LayoutElement();
        content.Name = 'Content';
        content.Visible = true;
        content.Row = 2;
        content.ElementType = 'content';
        content['CellOverflow'] = Overflow.hidden;
        l.Elements.push(content);

        return l;
    }
}

export class NavigationStructureItem {
    private NavigationNode: any;
    private UnsortedChildren: NavigationStructureItem[][] = [];

    get ID() {
        return this.NavigationNode._Id;
    }
    get PreviousSibbling() {
        return this.NavigationNode.PreviousSibbling;
    }
    get ParentID() {
        return this.NavigationNode.ParentID;
    }

    constructor(nn: any) {
        this.NavigationNode = nn;
    }

    AddChild(child: NavigationStructureItem) {
        for (let i = 0; i < this.UnsortedChildren.length; i++) {
            const childList = this.UnsortedChildren[i];
            const first = childList[0].PreviousSibbling;
            if (first) {
                if (child.ID === first) {
                    childList.splice(0, 0, child);
                    for (let j = 0; j < this.UnsortedChildren.length; j++) {
                        if (j !== i) {
                            const innerList = this.UnsortedChildren[j];
                            const lastID = innerList[innerList.length - 1].ID;
                            if (child.PreviousSibbling === lastID) {
                                innerList.push(...childList);
                                this.UnsortedChildren.splice(i, 1);
                                return;
                            }
                        }
                    }
                    return;
                }
            }
            const last = childList[childList.length - 1].ID;
            if (child.PreviousSibbling === last) {
                childList.push(child);
                for (let j = 0; j < this.UnsortedChildren.length; j++) {
                    if (j !== i) {
                        const innerList = this.UnsortedChildren[j];
                        const innerFirst = innerList[0];
                        if (innerFirst.PreviousSibbling === child.ID) {
                            childList.push(...innerList);
                            this.UnsortedChildren.splice(j, 1);
                            return;
                        }
                    }
                }
                return;
            }
        }
        this.UnsortedChildren.push([child]);
    }

    GetStructure() {
        const retVal = {
            Data: this.NavigationNode,
            Children: []
        };
        let lastID: any;
        if (this.UnsortedChildren.some(x => {
            if (!x[0].PreviousSibbling) {
                x.forEach(y => {
                    retVal.Children.push(y.GetStructure());
                });
                lastID = x[x.length - 1].ID;
                return true;
            }
            return false;
        })) {
            while (this.UnsortedChildren.some(x => {
                if (x[0].PreviousSibbling === lastID) {
                    x.forEach(y => {
                        retVal.Children.push(y.GetStructure());
                    });
                    lastID = x[x.length - 1].ID;
                    return true;
                }
                return false;
            })) { }
        }
        return retVal;
    }
}
